import type {
  Entity,
  PagedResponse,
} from "@mobilemind/common/src/types/utilities"
import { reportApi } from "./api"

export type OrganizationGroup = {
  id: string
  label: string
}

type OrganizationGroupResource = Omit<Entity, "attributes" | "type"> & {
  type: "group--group"
  attributes?: {
    label?: string
  }
}

type OrganizationGroupRelationshipResponse = PagedResponse<Entity> & {
  included?: OrganizationGroupResource[]
}

const GROUP_PAGE_SIZE = 50

export const groupsApi = reportApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizationGroups: builder.query<OrganizationGroup[], string>({
      queryFn: async (organizationId, _api, _extraOptions, baseQuery) => {
        const groups = new Map<string, OrganizationGroup>()
        let offset = 0
        let hasMore = true

        while (hasMore) {
          const result = await baseQuery({
            url: "/group_content/organization-subgroup-group",
            params: {
              "filter[gid.id]": organizationId,
              include: "entity_id",
              "page[offset]": offset,
              "page[limit]": GROUP_PAGE_SIZE,
            },
            headers: {
              Accept: "application/vnd.api+json",
            },
          })

          if (result.error) {
            return { error: result.error }
          }

          const response = result.data as OrganizationGroupRelationshipResponse
          const relationshipsOnPage = response.data.length

          response.included?.forEach((group) => {
            if (group.type !== "group--group") {
              return
            }

            groups.set(group.id, {
              id: group.id,
              label: group.attributes?.label || "Untitled group",
            })
          })

          offset += relationshipsOnPage
          const total = Number(response.meta.count)
          hasMore = relationshipsOnPage > 0 && offset < total
        }

        return {
          data: Array.from(groups.values()).sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
          ),
        }
      },
    }),
  }),
})

export const { useGetOrganizationGroupsQuery } = groupsApi
